/**
 * Global In-Memory Audio Store
 * 
 * Stores pre-generated audio blob URLs in memory for INSTANT playback (0ms lookup).
 * Persists across React route changes (module-level variable).
 * Also uses sessionStorage as BACKUP for bulletproof cross-navigation transfer.
 * 
 * Flow:
 * 1. LoadingStory generates TTS audio → saves blob URL here + base64 to IndexedDB
 * 2. PastStory reads from here FIRST (instant) → sessionStorage backup → IndexedDB (slow) → API (slowest)
 */

const audioStore = {};

// Expose on window for debugging
if (typeof window !== 'undefined') {
  window.__audioStore = audioStore;
}

/**
 * Save audio to in-memory store + sessionStorage backup
 */
export const saveToGlobalStore = (language, chunkText, blobUrl, dataUrl) => {
  const key = `${language}_${chunkText.substring(0, 40).replace(/\s+/g, '_')}`;
  audioStore[key] = { blobUrl, dataUrl };
  
  // BACKUP: Also save dataUrl to sessionStorage (survives any navigation issue)
  try {
    if (dataUrl && dataUrl.length < 200000) { // Only save if < 200KB to avoid quota
      sessionStorage.setItem(`as_${key}`, dataUrl);
    }
  } catch (e) { /* sessionStorage full or unavailable */ }
  
  if (Object.keys(audioStore).length <= 3 || Object.keys(audioStore).length % 20 === 0) {
    console.log(`[GlobalStore] Saved: ${key} (total: ${Object.keys(audioStore).length})`);
  }
};

/**
 * Get audio from in-memory store — SYNCHRONOUS, 0ms latency
 * Falls back to sessionStorage if in-memory store is empty
 */
export const getFromGlobalStore = (language, chunkText) => {
  const key = `${language}_${chunkText.substring(0, 40).replace(/\s+/g, '_')}`;
  
  // Primary: in-memory store
  const entry = audioStore[key];
  if (entry) return entry;
  
  // BACKUP: sessionStorage (survives navigation issues)
  try {
    const ssData = sessionStorage.getItem(`as_${key}`);
    if (ssData) {
      // Restore to in-memory store — create blobUrl for instant playback
      let blobUrl = null;
      try {
        const b64 = ssData.startsWith('data:') ? ssData.split(',')[1] : ssData;
        if (b64) {
          const byteChars = atob(b64);
          const byteArr = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
          blobUrl = URL.createObjectURL(new Blob([byteArr], { type: 'audio/mpeg' }));
        }
      } catch (_) { /* blob creation non-fatal */ }
      const dataUrl = ssData.startsWith('data:') ? ssData : `data:audio/mpeg;base64,${ssData}`;
      audioStore[key] = { blobUrl, dataUrl };
      return audioStore[key];
    }
  } catch (e) { /* sessionStorage unavailable */ }
  
  return null;
};

/**
 * Check if audio exists in store
 */
export const hasInGlobalStore = (language, chunkText) => {
  const key = `${language}_${chunkText.substring(0, 40).replace(/\s+/g, '_')}`;
  if (audioStore[key]) return true;
  try { return !!sessionStorage.getItem(`as_${key}`); } catch (e) { return false; }
};

/**
 * Get store stats
 */
export const getStoreStats = () => {
  const inMemory = Object.keys(audioStore).length;
  let inSession = 0;
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      if (sessionStorage.key(i)?.startsWith('as_')) inSession++;
    }
  } catch (e) {}
  
  return {
    totalChunks: Math.max(inMemory, inSession),
    inMemory,
    inSession,
    languages: {
      hindi: Object.keys(audioStore).filter(k => k.startsWith('hindi_')).length,
      hinglish: Object.keys(audioStore).filter(k => k.startsWith('hinglish_')).length,
      english: Object.keys(audioStore).filter(k => k.startsWith('english_')).length,
    }
  };
};

/**
 * Get all stored keys (for debugging)
 */
export const getAllKeys = () => Object.keys(audioStore);

/**
 * Clear store (for memory cleanup on logout)
 */
export const clearGlobalStore = () => {
  // Revoke blob URLs to free memory
  Object.values(audioStore).forEach(entry => {
    if (entry.blobUrl && entry.blobUrl.startsWith('blob:')) {
      try { URL.revokeObjectURL(entry.blobUrl); } catch (_) {}
    }
  });
  Object.keys(audioStore).forEach(k => delete audioStore[k]);
  // Clear sessionStorage backup
  try {
    const keysToRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith('as_')) keysToRemove.push(k);
    }
    keysToRemove.forEach(k => sessionStorage.removeItem(k));
  } catch (e) {}
};
